import { ContextMenuButton, Td, Tr } from 'app/components/Table';
import { transparentize } from '@chakra-ui/theme-tools';
import { ReactComponent as NotesIcon } from 'resources/images/notes.svg';
import { ReactComponent as LineIcon } from 'resources/images/line.svg';
import { MouseEvent, useState } from 'react';
import { ListItemText } from 'app/components/ChakraTableList';
import { PlaylistAudio } from 'types/playlists.types';
import { getFormattedDuration } from 'app/components/Song/Audio/AudioTableList/utils';
import { AudioPlayControls } from 'app/components/Song/Audio/AudioPlayControls';
import { Center, IconButton } from '@chakra-ui/react';
import { useAudioPlayHook } from 'app/components/Song/Audio/AudioPlayControls/AudioPlayHook';
import {
  DragSourceMonitor,
  DropTargetMonitor,
  useDrag,
  useDrop,
} from 'react-dnd';
import { showRowTopShadow } from 'app/components/DraggableListRow/utils';
import { DebouncedFuncLeading } from 'lodash';
import { AudioTitle } from 'app/components/Song/Audio/AudioTitle';
import { AudioDialogState } from './types';

interface PlaylistAudioRowProps {
  audio: PlaylistAudio;
  loadPlaylist: (playIndex: number) => void;
  setNoteDialog: (value: React.SetStateAction<AudioDialogState>) => void;
  index: number;
  onDragging: DebouncedFuncLeading<(rowIndex: number) => void>;
  onRowReorder?: (
    sourceIndex: number,
    targetIndex: number,
    isTop: boolean
  ) => void;
  contextHandler: (e: MouseEvent<HTMLDivElement | HTMLButtonElement>) => void;
}

export const PlaylistAudioRow = ({
  audio,
  loadPlaylist,
  setNoteDialog,
  index: rowIndex,
  onDragging,
  onRowReorder,
  contextHandler,
}: PlaylistAudioRowProps) => {
  const isLoading = ({ isAudioLoading, audioPlayerGuid }) =>
    isAudioLoading && audio.guid === audioPlayerGuid;

  const audioFileDuration = getFormattedDuration(audio.audio.duration);
  const audioGuid = audio.audio.guid;
  const parentGuid = audio.song.guid;
  const songDetails = { title: audio.song.title };
  const playlistAudioGuid = audio.guid;
  const handleAudioLoad = () => loadPlaylist(rowIndex);
  const audioPlay = useAudioPlayHook({
    audioGuid,
    parentGuid,
    songDetails,
    handleAudioLoad,
    playlistAudioGuid,
  });

  const [isTop, setIsTop] = useState(false);
  let divRef: HTMLDivElement | null;

  const [{ isDragging }, drag] = useDrag({
    type: 'ROW_DRAG',
    item: { index: rowIndex },
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ isOver }, drop] = useDrop({
    accept: 'ROW_DRAG',
    drop({ index }: { index: number }) {
      onRowReorder?.(index, rowIndex, isTop);
    },
    hover: (
      item: {
        index: number;
      },
      monitor: DropTargetMonitor
    ) => {
      if (onDragging) {
        onDragging(item.index);
      }
      setIsTop(showRowTopShadow(divRef, monitor));
    },
    collect: (monitor: DropTargetMonitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  return (
    <Tr
      ref={(ref) => {
        if (ref) {
          divRef = ref;
        }
        drag(drop(ref));
      }}
      userSelect="none"
      zIndex={1}
      position="relative"
      role="row"
      data-group
      onContextMenu={contextHandler}
      onDoubleClick={audioPlay.handlePlay}
      sx={{
        ...(isDragging
          ? {
              bg: transparentize('M.10', 0.36),
            }
          : {}),
        ...(isOver
          ? {
              bg: 'transparent !important',
              cursor: 'grabbing !important',
            }
          : {}),
        ...(isOver
          ? {
              rounded: 'none',
              boxShadow: isTop
                ? `inset 0px 1px 0px 0px var(--chakra-colors-B-80)`
                : `0px 1px 0px 0px var(--chakra-colors-B-80)`,
            }
          : {}),
      }}
    >
      <Td position="relative">
        {audio.audio.guid ? (
          <>
            <AudioPlayControls
              isLoading={isLoading}
              audioGuid={audioGuid}
              playlistAudioGuid={audio.guid}
              parentGuid={parentGuid}
              songDetails={songDetails}
              handleAudioLoad={handleAudioLoad}
              sx={{
                zIndex: 1,
                visibility: audioPlay.isActive ? 'visible' : 'hidden',
                _groupHover: {
                  visibility: 'visible',
                },
              }}
            />
            {audioPlay.isActive ? (
              ''
            ) : (
              <Center position="absolute" top={0} lineHeight="48px" minW={5}>
                {rowIndex + 1}
              </Center>
            )}
          </>
        ) : (
          <Center>{rowIndex}</Center>
        )}
      </Td>
      <Td>
        <AudioTitle
          audioGuid={audio.audio.guid}
          playlistAudioGuid={audio.guid}
          title={audio.title || audio.audio.title}
          isLatestVersion={false}
        />
      </Td>
      <Td>
        <ListItemText
          primary={audio.song.title}
          title={audio.song.title}
          data-testid="song-title"
        />
      </Td>
      <Td>
        {audio.note ? (
          <ListItemText
            title={audio.note}
            primary={audio.note}
            data-testid="audio-note-button"
            primaryTextProps={{
              cursor: 'pointer',
              color: 'textSubdued',
              maxWidth: '255px',
              onClick: () => {
                setNoteDialog({ open: true, audio });
              },
            }}
          />
        ) : (
          <IconButton
            aria-label="audio note"
            variant="text"
            color="baseText"
            size="sm"
            minW="unset"
            data-testid="audio-note-button"
            onClick={() => setNoteDialog({ open: true, audio })}
          >
            {audio.note ? <NotesIcon /> : <LineIcon />}
          </IconButton>
        )}
      </Td>
      <Td>
        <ListItemText
          primary={audioFileDuration || ''}
          data-testid="audio-duration"
        />
      </Td>
      <Td className="gap" />
      <Td>
        <ContextMenuButton onClick={contextHandler} />
      </Td>
    </Tr>
  );
};
